(() => {
  if (window.OztechSections) return;

  const elementsWithin = (root, selector) => {
    const elements = [];
    if (root instanceof Element && root.matches(selector)) elements.push(root);
    root.querySelectorAll?.(selector).forEach((element) => elements.push(element));
    return elements;
  };

  const initHeader = (header) => {
    if (header.oztechController) return;
    const controller = new AbortController();
    const { signal } = controller;
    const button = header.querySelector('[data-oz-menu-button]');
    const menu = header.querySelector('[data-oz-mobile-menu]');
    if (!button || !menu) return;

    const closeMenu = () => {
      menu.classList.remove('is-open');
      button.classList.remove('is-open');
      button.setAttribute('aria-expanded', 'false');
      document.documentElement.style.overflow = '';
    };

    button.addEventListener('click', () => {
      const open = menu.classList.toggle('is-open');
      button.classList.toggle('is-open', open);
      button.setAttribute('aria-expanded', open ? 'true' : 'false');
      document.documentElement.style.overflow = open ? 'hidden' : '';
    }, { signal });

    menu.querySelectorAll('a').forEach((link) => {
      link.addEventListener('click', closeMenu, { signal });
    });

    header.oztechController = controller;
  };

  const initProductGallery = (gallery) => {
    if (gallery.oztechController) return;
    const controller = new AbortController();
    const { signal } = controller;
    const slides = [...gallery.querySelectorAll('[data-oz-slide]')];
    const thumbs = [...gallery.querySelectorAll('[data-oz-thumb]')];
    const prev = gallery.querySelector('[data-oz-arrow="prev"]');
    const next = gallery.querySelector('[data-oz-arrow="next"]');
    const viewport = gallery.querySelector('.oz-product-gallery__viewport');
    if (!slides.length) return;
    let active = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
    let startX = 0;

    const show = (index, scrollThumb = true) => {
      if (index < 0) index = slides.length - 1;
      if (index >= slides.length) index = 0;
      active = index;

      slides.forEach((slide, slideIndex) => {
        const selected = slideIndex === active;
        slide.classList.toggle('is-active', selected);
        if (slide.hasAttribute('data-oz-image-viewer-trigger')) {
          slide.tabIndex = selected ? 0 : -1;
        }
      });
      thumbs.forEach((thumb, thumbIndex) => {
        thumb.classList.toggle('is-active', thumbIndex === active);
        thumb.setAttribute('aria-selected', thumbIndex === active ? 'true' : 'false');
      });
      if (scrollThumb) {
        thumbs[active]?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
      }
    };

    prev?.addEventListener('click', () => show(active - 1), { signal });
    next?.addEventListener('click', () => show(active + 1), { signal });
    thumbs.forEach((thumb, index) => thumb.addEventListener('click', () => show(index), { signal }));
    viewport?.addEventListener('touchstart', (event) => {
      startX = event.changedTouches[0].clientX;
    }, { passive: true, signal });
    viewport?.addEventListener('touchend', (event) => {
      const distance = startX - event.changedTouches[0].clientX;
      if (Math.abs(distance) < 45) return;
      show(distance > 0 ? active + 1 : active - 1);
    }, { passive: true, signal });

    gallery.oztechController = controller;
    show(active, false);
  };

  const initAgProduct = (section) => {
    if (section.oztechController) return;
    const controller = new AbortController();
    const { signal } = controller;
    const thumbs = [...section.querySelectorAll('[data-oz-thumb]')];
    const media = [...section.querySelectorAll('[data-oz-media]')];
    const prev = section.querySelector('.oz-ag-gallery__arrow--prev');
    const next = section.querySelector('.oz-ag-gallery__arrow--next');
    const variantSelect = section.querySelector('.oz-ag-buy__variant');
    const price = section.querySelector('[data-oz-price]');
    const addButton = section.querySelector('[data-oz-add]');
    const quoteButton = section.querySelector('[data-oz-quote]');
    const noPriceText = section.dataset.noPriceText || '';
    let currentIndex = Math.max(0, media.findIndex((item) => item.classList.contains('is-active')));

    const showMedia = (index) => {
      if (!media.length) return;
      if (index < 0) index = media.length - 1;
      if (index >= media.length) index = 0;
      currentIndex = index;
      const activeId = media[currentIndex].dataset.ozMedia;

      media.forEach((item) => {
        const selected = item.dataset.ozMedia === activeId;
        item.classList.toggle('is-active', selected);
        if (item.hasAttribute('data-oz-image-viewer-trigger')) item.tabIndex = selected ? 0 : -1;
      });
      thumbs.forEach((thumb) => {
        const selected = thumb.dataset.ozThumb === activeId;
        thumb.classList.toggle('is-active', selected);
        thumb.setAttribute('aria-selected', selected ? 'true' : 'false');
      });
    };

    const updateVariant = () => {
      if (!variantSelect) return;
      const option = variantSelect.options[variantSelect.selectedIndex];
      if (!option) return;
      const priceCents = Number.parseInt(option.dataset.priceCents || '0', 10);
      const available = option.dataset.available === 'true';
      if (price) price.textContent = priceCents > 0 ? option.dataset.price : noPriceText;
      if (addButton) {
        addButton.classList.toggle('is-hidden', priceCents <= 0);
        addButton.disabled = !available;
        addButton.textContent = available ? 'ADD TO CART' : 'SOLD OUT';
      }
      quoteButton?.classList.toggle('oz-ag-button--primary', priceCents <= 0);
      quoteButton?.classList.toggle('oz-ag-button--secondary', priceCents > 0);
    };

    thumbs.forEach((thumb, index) => thumb.addEventListener('click', () => showMedia(index), { signal }));
    prev?.addEventListener('click', () => showMedia(currentIndex - 1), { signal });
    next?.addEventListener('click', () => showMedia(currentIndex + 1), { signal });
    variantSelect?.addEventListener('change', updateVariant, { signal });

    section.oztechController = controller;
    showMedia(currentIndex);
    updateVariant();
  };

  const openVideo = (button) => {
    const section = button.closest('.shopify-section') || document;
    const modal = section.querySelector('[data-oz-video-modal]');
    const iframe = modal?.querySelector('[data-oz-video-frame]');
    if (!modal || !iframe || !button.dataset.ozVideoUrl) return;
    iframe.src = button.dataset.ozVideoUrl;
    modal.classList.add('is-active');
    document.body.style.overflow = 'hidden';
    modal.querySelector('[data-oz-video-close]')?.focus({ preventScroll: true });
  };

  const closeVideo = (modal) => {
    const iframe = modal.querySelector('[data-oz-video-frame]');
    modal.classList.remove('is-active');
    if (iframe) iframe.src = '';
    document.body.style.overflow = '';
  };

  const init = (root = document) => {
    elementsWithin(root, '[data-oz-header]').forEach(initHeader);
    elementsWithin(root, '[data-oz-product-gallery]').forEach(initProductGallery);
    elementsWithin(root, '[data-oz-ag-product]').forEach(initAgProduct);
  };

  const destroy = (root) => {
    const openHeader = root.querySelector?.('[data-oz-header] [data-oz-mobile-menu].is-open');
    const openVideo = root.querySelector?.('[data-oz-video-modal].is-active');

    elementsWithin(root, '[data-oz-header], [data-oz-product-gallery], [data-oz-ag-product]').forEach((element) => {
      element.oztechController?.abort();
      delete element.oztechController;
    });

    if (openHeader) document.documentElement.style.overflow = '';
    if (openVideo) document.body.style.overflow = '';
  };

  document.addEventListener('click', (event) => {
    const videoButton = event.target.closest('[data-oz-video-url]');
    if (videoButton) {
      openVideo(videoButton);
      return;
    }
    const modal = event.target.closest('[data-oz-video-modal]');
    if (modal && (event.target === modal || event.target.closest('[data-oz-video-close]'))) closeVideo(modal);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    document.querySelectorAll('[data-oz-video-modal].is-active').forEach(closeVideo);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroy(event.target));

  window.OztechSections = { init, destroy };
})();
